import React, { useState, useRef, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Grid,
} from "@mui/material"
import {
  Close as CloseIcon,
  Print as PrintIcon,
  FileDownload as DownloadIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material"
import { useReactToPrint } from "react-to-print"
import * as XLSX from "xlsx"
import { useApiQuery } from "../../hooks/useApi"
import { useQueryClient } from "@tanstack/react-query"

function AdminReportGenerator({ open, onClose }) {
  const [filterType, setFilterType] = useState("month")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const reportRef = useRef()

  const reportUrl = useMemo(() => {
    const params = new URLSearchParams()
    
    if (filterType === "custom" && startDate && endDate) {
      params.append("startDate", startDate)
      params.append("endDate", endDate)
    } else {
      params.append("filter", filterType)
    }
    
    return `/admin/report?${params.toString()}`
  }, [filterType, startDate, endDate])

  const queryClient = useQueryClient()

  const {
    data: reportData,
    error,
    isFetching,
    refetch,
  } = useApiQuery(["admin-report", reportUrl], reportUrl, {
    enabled: false,
  })

  const handleGenerateReport = () => {
    refetch()
  }

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Admin_Report_${new Date().toISOString().split("T")[0]}`,
  })

  const handleDownloadExcel = () => {
    if (!reportData) return

    const workbook = XLSX.utils.book_new()

    // Summary Sheet
    const summaryData = [
      ["Comprehensive Admin Report"],
      [""],
      ["Report Period", `${new Date(reportData.dateRange.start).toLocaleDateString()} - ${new Date(reportData.dateRange.end).toLocaleDateString()}`],
      [""],
      ["=== SYSTEM OVERVIEW ==="],
      ["Total Clients", reportData.totalClients],
      ["Total Jobs (All Time)", reportData.totalJobs],
      ["Total Users", reportData.totalUsers],
      ["Total Completed Jobs (All Time)", reportData.totalCompletedJobs],
      [""],
      ["=== PERIOD STATISTICS ==="],
      ["New Jobs", reportData.newJobsToday],
      ["Jobs in Processing", reportData.processingJobsToday],
      ["Completed Jobs", reportData.completedJobsToday],
      ["Cancelled Jobs", reportData.cancelledJobsToday],
      [""],
      ["=== FINANCIAL DATA ==="],
      ["Receipts Created", reportData.receiptsCreatedToday],
      ["Total Receipts Amount", `₹${reportData.totalReceiptsAmount?.toLocaleString()}`],
      ["Jobs Settled", reportData.jobsSettledToday],
      ["Total Settled Amount", `₹${reportData.totalSettledAmount?.toLocaleString()}`],
      ["Balance Carried Forward", `₹${reportData.totalBalanceCarried?.toLocaleString()}`],
      [""],
      ["=== RECEIPTS BY PAYMENT METHOD ==="],
      ["Payment Method", "Count", "Amount"],
    ]

    if (reportData.receiptsByPaymentMethod) {
      Object.entries(reportData.receiptsByPaymentMethod).forEach(([method, data]) => {
        summaryData.push([
          method.toUpperCase().replace('_', ' '),
          data.count,
          data.total,
        ])
      })
    }

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData)
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary")

    // Jobs Sheet
    if (reportData.jobs && reportData.jobs.length > 0) {
      const jobsData = [
        ["Job ID", "Client", "Status", "Fees", "Fee Status", "Created At", "Assigned To"],
      ]

      reportData.jobs.forEach((job) => {
        jobsData.push([
          job.jobId,
          job.clientName,
          job.status,
          job.fees,
          job.feeStatus,
          new Date(job.createdAt).toLocaleString(),
          job.assignedTo,
        ])
      })

      const jobsSheet = XLSX.utils.aoa_to_sheet(jobsData)
      XLSX.utils.book_append_sheet(workbook, jobsSheet, "Jobs")
    }

    // Receipts Sheet
    if (reportData.receipts && reportData.receipts.length > 0) {
      const receiptsData = [
        ["Receipt #", "Date", "Client", "Amount", "Payment Method", "Bank", "Collected By"],
      ]

      reportData.receipts.forEach((receipt) => {
        receiptsData.push([
          receipt.receiptNumber,
          new Date(receipt.collectedDate).toLocaleString(),
          receipt.clientName,
          receipt.amount,
          receipt.paymentMethod,
          receipt.bank || "N/A",
          receipt.collectedBy,
        ])
      })

      const receiptsSheet = XLSX.utils.aoa_to_sheet(receiptsData)
      XLSX.utils.book_append_sheet(workbook, receiptsSheet, "Receipts")
    }

    // Settlements Sheet
    if (reportData.settlements && reportData.settlements.length > 0) {
      const settlementsData = [
        ["Date", "Job ID", "Client", "Amount Settled", "Balance Carried", "Settled By"],
      ]

      reportData.settlements.forEach((settlement) => {
        settlementsData.push([
          new Date(settlement.createdAt).toLocaleString(),
          settlement.jobId,
          settlement.clientName,
          settlement.totalUsed,
          settlement.balanceCarried,
          settlement.settledBy,
        ])
      })

      const settlementsSheet = XLSX.utils.aoa_to_sheet(settlementsData)
      XLSX.utils.book_append_sheet(workbook, settlementsSheet, "Settlements")
    }

    XLSX.writeFile(workbook, `Admin_Report_${new Date().toISOString().split("T")[0]}.xlsx`)
  }

  const handleDownloadPDF = () => {
    handlePrint()
  }

  const getFilterLabel = () => {
    switch (filterType) {
      case "today":
        return "Today"
      case "30days":
        return "Last 30 Days"
      case "month":
        return "This Month"
      case "all":
        return "All Time"
      case "custom":
        return "Custom Range"
      default:
        return "This Month"
    }
  }

  const handleClose = () => {
    queryClient.removeQueries(["admin-report", reportUrl])
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            Generate Admin Report
          </Typography>
          <Button onClick={handleClose} color="inherit">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {!reportData && (
          <Box sx={{ mb: 3 }}>
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Report Period</InputLabel>
                <Select
                  value={filterType}
                  label="Report Period"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="30days">Last 30 Days</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="custom">Custom Date Range</MenuItem>
                </Select>
              </FormControl>

              {filterType === "custom" && (
                <Stack direction="row" spacing={2}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>
              )}

              {error && (
                <Alert severity="error">
                  {error.message}
                </Alert>
              )}

              <Button
                variant="contained"
                size="large"
                onClick={handleGenerateReport}
                disabled={isFetching || (filterType === "custom" && (!startDate || !endDate))}
                startIcon={isFetching ? <CircularProgress size={20} /> : <CalendarIcon />}
              >
                {isFetching ? "Generating..." : "Generate Report"}
              </Button>
            </Stack>
          </Box>
        )}

        {reportData && (
          <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={handleDownloadPDF}>
                Print / Save as PDF
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadExcel}>
                Download Excel
              </Button>
            </Stack>

            <Box ref={reportRef} sx={{ p: 3, bgcolor: "white" }}>
              {/* Report Header */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Comprehensive Admin Report
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  {new Date(reportData.dateRange.start).toLocaleDateString()} -{" "}
                  {new Date(reportData.dateRange.end).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Generated on: {new Date().toLocaleString()}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* System Overview */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  System Overview
                </Typography>
                <Grid container spacing={2}>
                  {/* <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: "primary.light" }}>
                      <Typography variant="body2" color="textSecondary">Total Clients</Typography>
                      <Typography variant="h4" fontWeight="bold">{reportData.totalClients}</Typography>
                    </Paper>
                  </Grid> */}
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: "secondary.light" }}>
                      <Typography variant="body2" color="textSecondary">Total Jobs</Typography>
                      <Typography variant="h4" fontWeight="bold">{reportData.totalJobs}</Typography>
                    </Paper>
                  </Grid>
                  {/* <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: "success.light" }}>
                      <Typography variant="body2" color="textSecondary">Total Users</Typography>
                      <Typography variant="h4" fontWeight="bold">{reportData.totalUsers}</Typography>
                    </Paper>
                  </Grid> */}
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: "info.light" }}>
                      <Typography variant="body2" color="textSecondary">Completed Jobs</Typography>
                      <Typography variant="h4" fontWeight="bold">{reportData.totalCompletedJobs}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Period Statistics */}
              {/* <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Period Statistics ({getFilterLabel()})
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="body2" color="textSecondary">New Jobs</Typography>
                      <Typography variant="h4" fontWeight="bold">{reportData.newJobsToday}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="body2" color="textSecondary">Processing</Typography>
                      <Typography variant="h4" fontWeight="bold">{reportData.processingJobsToday}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="body2" color="textSecondary">Completed</Typography>
                      <Typography variant="h4" fontWeight="bold">{reportData.completedJobsToday}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="body2" color="textSecondary">Cancelled</Typography>
                      <Typography variant="h4" fontWeight="bold">{reportData.cancelledJobsToday}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box> */}

              {/* Financial Summary */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Financial Summary
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Receipts Created</strong></TableCell>
                        <TableCell align="right">{reportData.receiptsCreatedToday}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Total Receipts Amount</strong></TableCell>
                        <TableCell align="right">₹{reportData.totalReceiptsAmount?.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Jobs Settled</strong></TableCell>
                        <TableCell align="right">{reportData.jobsSettledToday}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Total Settled Amount</strong></TableCell>
                        <TableCell align="right">₹{reportData.totalSettledAmount?.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Balance Carried Forward</strong></TableCell>
                        <TableCell align="right">₹{reportData.totalBalanceCarried?.toLocaleString()}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Payment Methods */}
              {reportData.receiptsByPaymentMethod && Object.keys(reportData.receiptsByPaymentMethod).length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Receipts by Payment Method
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Payment Method</strong></TableCell>
                          <TableCell align="right"><strong>Count</strong></TableCell>
                          <TableCell align="right"><strong>Amount</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                      {Object.entries(reportData.receiptsByPaymentMethod).map(
                        ([method, data]) => (
                          <React.Fragment key={method}>
                            {/* Payment Method Summary Row */}
                            <TableRow>
                              <TableCell>
                                <strong>{method.toUpperCase().replace("_", " ")}</strong>
                              </TableCell>
                              <TableCell align="right">{data.count}</TableCell>
                              <TableCell align="right">
                                ₹{data.total.toLocaleString()}
                              </TableCell>
                            </TableRow>

                            {/* Bank-wise Breakdown */}
                            {data.banks &&
                              Object.entries(data.banks).map(([bank, amount]) => (
                                <TableRow key={bank}>
                                  <TableCell sx={{ pl: 4 }}>
                                     {bank}
                                  </TableCell>
                                  <TableCell align="right">—</TableCell>
                                  <TableCell align="right">
                                    ₹{amount.toLocaleString()}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </React.Fragment>
                        )
                      )}
                    </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Footer */}
              <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid #ddd", textAlign: "center" }}>
                <Typography variant="caption" color="textSecondary">
                  This is a computer-generated report. For any queries, please contact the administration.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default AdminReportGenerator